import { useCallback, useState } from 'react';
import { Avatar, Box, Button, Chip, IconButton, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import type { GrupoCatalogo, LojaAtualResponse } from '../../../api';
import CategoriasLateral from '../../../components/catalogo-kiosk/categorias-lateral';
import GradeProdutos, { type CategoriaSelecionada } from '../../../components/catalogo-kiosk/grade-produtos';
import DialogPassos from '../../../components/catalogo-kiosk/dialog-passos';
import DialogDetalheProduto from '../../../components/catalogo-kiosk/dialog-detalhe-produto';
import { TemaKiosk } from '../../../components/catalogo-kiosk/tema-kiosk';
import { CardapioVazioKiosk } from '../../../components/catalogo-kiosk/vazio';
import { DialogBusca } from '../../../components/catalogo-kiosk/dialog-busca';
import { RodapeCarrinho } from '../../../components/catalogo-kiosk/rodape-carrinho';
import { useCardapioKiosk } from '../../../components/catalogo-kiosk/use-cardapio-kiosk';
import { SOBRE_EMBUTIDO, SobreCardapio } from '../../../components/sobre-cardapio';
import { destinoChamadoConfigurado } from '../../../lib/destino-chamado';
import { getTabletConfig } from '../../../lib/tablet-config';
import { abrirChamarGarcom, abrirConta, ESTILO_BOTAO_CONTA, ESTILO_BOTAO_GARCOM } from '../../../lib/tablet-eventos';
import { medidasDoAparelho } from './medidas';

interface CatalogoKioskProps {
  loja: LojaAtualResponse;
  grupos: GrupoCatalogo[];
  aoAbrirCarrinho: () => void;
  aoFinalizar: () => void;
}

/**
 * Cardápio do TABLET de mesa: categorias na lateral, grade de produtos grande e
 * personalização por passos num wizard, sem sair da tela. O totem de pedidos tem
 * o seu em `pages/totem/pedidos/catalogo` — os dois dividem as peças de
 * `components/catalogo-kiosk` e o `useCardapioKiosk`, e cada um desenha a sua tela.
 */
export function CatalogoKiosk({ loja, grupos, aoAbrirCarrinho, aoFinalizar }: CatalogoKioskProps): JSX.Element {
  const cardapio = useCardapioKiosk(grupos, { seletorCompacto: medidasDoAparelho(loja.identificador).seletorCompacto, detalhesNaFoto: true });
  const { carrinho } = cardapio;

  const [categoria, setCategoria] = useState<CategoriaSelecionada>(null);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const abrirBusca = useCallback(() => setBuscaAberta(true), []);

  // Tablet em pé: a lateral disputa a largura com a grade e o nome da categoria
  // quebra em duas linhas, o que empurra cada botão para perto de 160px — cabiam
  // poucos grupos sem rolar. Em retrato ele usa medidas mais compactas.
  const emPe = useMediaQuery('(orientation: portrait)');
  const medidas = medidasDoAparelho(loja.identificador, emPe);
  const mesaTablet = getTabletConfig().mesa;
  // Tablet de 8" em pé tem ~600px: com garçom + conta por extenso o cabeçalho
  // estoura e empurra o nome da loja para fora.
  const cabecalhoApertado = useMediaQuery('(max-width: 899px)');

  return (
    <TemaKiosk>
      <Stack
        sx={{
          width: '100%',
          // 100vh no Safari do iPad conta a barra do navegador: o rodapé com o
          // total cai abaixo da dobra e, como a tela não rola, fica inalcançável.
          height: '100vh',
          '@supports (height: 100dvh)': { height: '100dvh' },
          overflow: 'hidden',
          boxSizing: 'border-box',
          // Aparelho de mesa: toque longo não deve selecionar texto nem abrir lupa.
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          '& input, & textarea': { userSelect: 'text' },
        }}
      >
        <Paper data-cabecalho-tablet elevation={0} square sx={{ flexShrink: 0, px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={loja.configuracao.urlLogo ?? undefined}
              variant="rounded"
              alt={loja.configuracao.nome}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                bgcolor: loja.configuracao.urlLogo ? 'transparent' : 'action.hover',
              }}
            >
              {!loja.configuracao.urlLogo && <StorefrontIcon />}
            </Avatar>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 700 }}>
                {loja.configuracao.nome}
              </Typography>

              {mesaTablet && (
                <Chip
                  size="small"
                  color="primary"
                  icon={<TableRestaurantIcon />}
                  label={`Mesa ${mesaTablet}`}
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Junto das outras ações do cabeçalho; de texto, porque não é ação de salão como Garçom e Conta. */}
            {cabecalhoApertado ? (
              <IconButton size="large" color="primary" onClick={abrirBusca} aria-label="Buscar" sx={{ flexShrink: 0 }}>
                <SearchIcon />
              </IconButton>
            ) : (
              <Button
                variant="text"
                size="large"
                startIcon={<SearchIcon />}
                onClick={abrirBusca}
                sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap', px: 2, textTransform: 'none' }}
              >
                Buscar
              </Button>
            )}

            {destinoChamadoConfigurado(loja) && (
              <Button
                {...ESTILO_BOTAO_GARCOM}
                size="large"
                startIcon={<RoomServiceIcon />}
                onClick={() => abrirChamarGarcom()}
                sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap', px: cabecalhoApertado ? 1.5 : 2, textTransform: 'none' }}
              >
                {cabecalhoApertado ? 'Garçom' : 'Chamar Garçom'}
              </Button>
            )}

            <Button
              {...ESTILO_BOTAO_CONTA}
              size="large"
              startIcon={<ReceiptLongIcon />}
              onClick={() => abrirConta()}
              sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap', px: cabecalhoApertado ? 1.5 : 2, textTransform: 'none' }}
            >
              Conta
            </Button>
          </Stack>
        </Paper>

        {grupos.length === 0 ? (
          <CardapioVazioKiosk />
        ) : (
          <Stack direction="row" sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <CategoriasLateral
              grupos={grupos}
              selectedCategoryId={categoria}
              onCategorySelect={setCategoria}
              largura={medidas.larguraLateral}
              alturaCategoria={medidas.alturaCategoria}
              tamanhoIcone={medidas.tamanhoIcone}
              fonteCategoria={medidas.fonteCategoria}
              indicadorRolagem={medidas.indicadorRolagem}
              alturaAtalho={medidas.alturaAtalho}
              rodape={SOBRE_EMBUTIDO}
            />

            <GradeProdutos
              grupos={grupos}
              selectedCategoryId={categoria}
              renderCard={cardapio.renderCard}
              larguraMinimaCard={medidas.larguraMinimaCard}
              fonteTitulo={medidas.fonteTituloGrade}
            />
          </Stack>
        )}

        <RodapeCarrinho
          compacto
          totalItens={carrinho.totalItens}
          subtotal={carrinho.subtotal}
          aoAbrirCarrinho={aoAbrirCarrinho}
          aoFinalizar={aoFinalizar}
        />

        {grupos.length === 0 && <SobreCardapio />}

        <DialogPassos
          produto={cardapio.produtoPersonalizar}
          permitirObservacao
          permitirAdicionalPorSabor
          largura="md"
          onFechar={cardapio.fecharPersonalizacao}
          onConfirmar={cardapio.confirmarPersonalizado}
        />

        <DialogDetalheProduto
          produto={cardapio.produtoDetalhe}
          aparelho="tablet"
          onFechar={cardapio.fecharDetalhes}
          onConfirmar={cardapio.confirmarDetalhe}
        />

        <DialogBusca
          aberto={buscaAberta}
          grupos={grupos}
          renderCard={cardapio.renderCard}
          larguraMinimaCard={medidas.larguraMinimaCard}
          aoFechar={() => setBuscaAberta(false)}
        />
      </Stack>
    </TemaKiosk>
  );
}
